import { useTranslation } from "react-i18next";
import { Button, Input, Select } from "../../../components/ui";
import {
  createMerchandiseDraft,
  MERCHANDISE_UNIT_TYPES,
  type MerchandiseDraft,
  type MerchandiseDraftErrors,
} from "../../../features/shipments/merchandiseDraft";

interface MerchandiseEditorProps {
  drafts: MerchandiseDraft[];
  onChange: (drafts: MerchandiseDraft[]) => void;
  errors?: Record<string, MerchandiseDraftErrors>;
  disabled?: boolean;
}

export default function MerchandiseEditor({
  drafts,
  onChange,
  errors = {},
  disabled = false,
}: MerchandiseEditorProps) {
  const { t } = useTranslation("envios");

  const unitOptions = MERCHANDISE_UNIT_TYPES.map((value) => ({
    value,
    label: t(`merchandise.units.${value}`),
  }));

  const update = (uid: string, patch: Partial<MerchandiseDraft>) =>
    onChange(drafts.map((draft) => (draft.uid === uid ? { ...draft, ...patch } : draft)));

  const remove = (uid: string) => onChange(drafts.filter((draft) => draft.uid !== uid));

  const errorText = (error: string | undefined) =>
    error ? t(`merchandise.errors.${error}`) : undefined;

  return (
    <div className="space-y-3">
      {drafts.map((draft, index) => {
        const itemErrors = errors[draft.uid] ?? {};
        return (
          <div key={draft.uid} className="space-y-3 rounded-xl border border-line bg-surface p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold text-content-secondary">
                {t("merchandise.itemN", { n: index + 1 })}
              </span>
              <Button
                variant="dangerSoft"
                size="sm"
                iconOnly
                disabled={disabled}
                icon={<i className="ri-delete-bin-line" />}
                aria-label={t("merchandise.remove")}
                onClick={() => remove(draft.uid)}
              />
            </div>

            <Input
              label={t("merchandise.description")}
              placeholder={t("merchandise.descriptionPlaceholder")}
              value={draft.description}
              disabled={disabled}
              error={errorText(itemErrors.description)}
              onChange={(event) => update(draft.uid, { description: event.target.value })}
            />

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input
                label={t("merchandise.quantity")}
                type="number"
                step="1"
                min="1"
                value={draft.quantity}
                disabled={disabled}
                error={errorText(itemErrors.quantity)}
                onChange={(event) => update(draft.uid, { quantity: event.target.value })}
              />
              <Select
                label={t("merchandise.unitType")}
                value={draft.unitType}
                options={unitOptions}
                disabled={disabled}
                onChange={(event) => update(draft.uid, { unitType: event.target.value })}
              />
              <Input
                label={t("merchandise.weight")}
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={draft.weight}
                disabled={disabled}
                error={errorText(itemErrors.weight)}
                onChange={(event) => update(draft.uid, { weight: event.target.value })}
              />
              <Input
                label={t("merchandise.volume")}
                type="number"
                step="0.001"
                min="0"
                placeholder="0.000"
                value={draft.volume}
                disabled={disabled}
                error={errorText(itemErrors.volume)}
                onChange={(event) => update(draft.uid, { volume: event.target.value })}
              />
            </div>
          </div>
        );
      })}

      <Button
        variant="outline"
        size="sm"
        disabled={disabled}
        icon={<i className="ri-add-line" />}
        onClick={() => onChange([...drafts, createMerchandiseDraft()])}
      >
        {t("merchandise.add")}
      </Button>
    </div>
  );
}
